/* @flow */
import { getterFromKeyPath } from 'trustar/utils';
import { createSelector } from 'reselect';
import type { OrderedMap, Map, Seq, List } from 'immutable';
import { toLower } from 'trustar/utils';

import { hyphenate, redactionCategories } from './constants.js';

export const loading = getterFromKeyPath(['redaction', 'loading']);
export const status = getterFromKeyPath(['redaction', 'status']);
export const currentCategoryId = getterFromKeyPath(['redaction', 'currentCategoryId']);
export const redactionTerms = getterFromKeyPath(['redaction', 'terms']);
export const redactionTermsTextMap = getterFromKeyPath(['redaction', 'termsTextMap']);
export const redactables = getterFromKeyPath(['redaction', 'redactables']);
export const allRedactables = getterFromKeyPath(['redaction', 'allRedactables']);

export const redactionTermList = createSelector(
  redactionTerms,
  (redactionTerms: OrderedMap<any, any>) => redactionTerms.toList(),
);

/**
 * For some reason using valueSeq here breaks the fixed data table.
 *
 * NOTE: Because we are using this in FixedDataTable we need to provide an
 * indexed data structure. Currently we're using List.
 */
export const currentTerms = createSelector(
  [ redactionTerms, currentCategoryId ],
  (redactionTerms: OrderedMap<any, any>, currentCategoryId: string) => {
    return redactionTerms
      .filter(redactionTerm => redactionTerm.get('categoryId') === currentCategoryId)
      .toList();
  },
);

/**
 * Currently we only use this for testing purposes. It can be useful to get a
 * term by ID. This is essentially just a convenience method since we could
 * always just evaluate the full term state and get one by ID.
 */
export const redactionTerm = (id: string) => {
  return createSelector(
    redactionTerms,
    (redactionTerms: OrderedMap<any, any>) => redactionTerms.get(id),
  );
};

/**
 * Get a term by its text. This is used during validation to avoid term
 * duplication. So far this is the entire point of the termTextMap store:
 * constant time checking whether a term exists.
 */
export const redactionTermExists = (text: string) => {
  return createSelector(
    redactionTermsTextMap,
    (map: Map<any, any>) => map.has(toLower(text)),
  );
};

const redactionTermSeq = createSelector(
  redactionTerms,
  (redactionTerms: OrderedMap<any, any>) => redactionTerms.valueSeq(),
);

export const truncatedCategoryList = createSelector(
  redactionTermSeq,
  (redactionTermSeq: Seq<any, any>) => {
    return redactionCategories.map(cat => {
      return cat.set('terms', redactionTermSeq.filter(redactionTerm => {
        return redactionTerm.get('categoryId') === cat.get('id');
      }));
    })
    .toList()
    .map(cat => cat.update('terms', t =>
      t.sort((a, b) =>
        toLower(a.get('text')).localeCompare(toLower(b.get('text'))))
      )
    );
  },
);

/**
 * The category lists also includes the number of terms for each category. In
 * this way it is not exactly representative of the categories list returned
 * from the server on fetch.
 */
export const categoryList = createSelector(
  redactionTerms,
  (redactionTerms: OrderedMap<any, any>) => {
    return redactionCategories.map(cat => {
      return cat.set('terms', redactionTerms.filter(redactionTerm => {
        return redactionTerm.get('categoryId') === cat.get('id');
      }));
    }).toList();
  },
);

/**
 * Build a Map of category names and their assocated terms so that we can
 * redact them from all report content.
 *
 * NOTE: The returned Map does not necessarily have a one-to-one mapping with
 * all categories. Only categories for which there is at least one term will be
 * added. So if there is only one term in the entire app then only one key will
 * be created with Map of one term as the value.
 */
export const redactionMap = createSelector(
  redactionTerms,
  (redactionTerms: OrderedMap<any, any>) => {
    return redactionTerms.map(redactionTerm => {
      if (redactionTerm.get('categoryId')) {
        const category = redactionCategories.getIn([redactionTerm.get('categoryId', ''), 'name'], '');
        return redactionTerm.set('categoryName', hyphenate(category));
      }
      return redactionTerm;
    })
    .sort((c, d) => {
      const lengthC = c.get('text').length;
      const lengthD = d.get('text').length;

      if (lengthC === lengthD) return 0;
      else if (lengthC > lengthD) return -1;
      else return 1;
    })
    .valueSeq();
  },
);

/* The status of ther current term import. */
export const redactablesMap = createSelector(
  [ redactables, allRedactables ],
  (redactables: OrderedMap<any, any>, allRedactables: List<any>) => {
    const allRedactablesText = allRedactables.map(v => v.get('text'));
    return redactables
      .filter(v => allRedactablesText.contains(v.get('text')))
      .toList();
  },
);



// WEBPACK FOOTER //
// ./src/app/modules/redaction/getters.js